import React, { ReactNode, useContext } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Box, Stepper } from '@wix/design-system';
import { AppContextProvider } from '../../../AppLoader';
import { EnforcementCard } from '../EnforcementCard';
import { ButtonWithLoader } from '../../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText } from '../../../ThemedComponents';
import { OwnerAppSelectDeviceBody } from './OwnerAppSelectDeviceBody';
import {
  OwnerAppConfirmPendingBody,
  OwnerAppConfirmErrorBody,
} from './OwnerAppConfirmBody';
import { OwnerAppBackupMethodContent } from './OwnerAppBackupMethodContent';
import { OwnerAppSuccessBody } from './OwnerAppSuccessBody';
import { dataHooks } from '../../../../stores/premiumUsers2FaEnforcement/dataHooks';
import { SendNotificationState } from '../../../../stores/premiumUsers2FaEnforcement/ownerApp/ownerApp';
import {
  ApprovalChallengeStatus,
  Steps,
} from '../../../../stores/premiumUsers2FaEnforcement/ownerApp/types';

interface StepConfig {
  primaryButton?: ReactNode;
  body: ReactNode;
}

/**
 * ID-2873: shell for the wix-2025 owner-app add-auth wizard — adding the Wix Owner app as
 * a 2FA method when the user already has the app (chose "Wix app" in
 * ChooseVerificationMethod; the no-app download path is `WixAppWizardRedesign`).
 * Steps on `ownerAppStore`: DeviceSelection → Confirmation → BackupMethod → Successful.
 */
export const OwnerAppAddAuthWizardRedesign: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: { premiumUsers2FaEnforcementStore: enforcementStore },
      displayStore: { preset },
    },
  } = useContext(AppContextProvider);
  const store = enforcementStore.ownerAppStore;
  const { t } = useTranslation();

  const isNotifying =
    store.sendNotificationState === SendNotificationState.Notifying;

  const stepperNode = (
    <Stepper
      dataHook={dataHooks.ownerAppModal.steps}
      type="text"
      activeStep={store.activeStep}
      steps={store.steps}
      onClick={store.onStepClick}
    />
  );

  const isPending =
    store.authStatus === ApprovalChallengeStatus.PENDING_APPROVAL ||
    store.authStatus === ApprovalChallengeStatus.APPROVED;

  const stepConfigs: Partial<Record<Steps, StepConfig>> = {
    [Steps.DeviceSelectionStep]: {
      primaryButton: (
        <Box
          direction="vertical"
          align="center"
          gap={2}
          width="100%"
          style={{ textAlign: 'center' }}
        >
          <ButtonWithLoader
            skin={preset.buttonDesign?.skin ?? 'standard'}
            showLoader={isNotifying}
            onClick={store.nextStep}
          >
            {t('addOwnerApp.wizard.primary')}
          </ButtonWithLoader>
          <ThemedText size="small" secondary>
            {t('addOwnerApp.wizard.choose.message')}
          </ThemedText>
        </Box>
      ),
      body: <OwnerAppSelectDeviceBody />,
    },
    [Steps.ConfirmationStep]: {
      body: isPending ? (
        <OwnerAppConfirmPendingBody />
      ) : (
        <OwnerAppConfirmErrorBody />
      ),
    },
    [Steps.BackupMethodStep]: {
      primaryButton: (
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          showLoader={isNotifying}
          onClick={store.nextStep}
        >
          {t('addOwnerApp.wizard.primary')}
        </ButtonWithLoader>
      ),
      body: <OwnerAppBackupMethodContent showTooltip={false} />,
    },
    [Steps.SuccessfulStep]: {
      body: <OwnerAppSuccessBody />,
    },
  };

  const config = stepConfigs[store.activeStep] ?? null;

  if (!config) {
    return null;
  }

  return (
    <EnforcementCard
      priority="primary"
      showBackButton
      backButtonCallback={store.onBackToChooseVerificationMethod}
      title={t('addOwnerApp.wizard.title')}
      subtitle={t('addOwnerApp.wizard.subtitle')}
      stepper={stepperNode}
      primaryButton={config.primaryButton}
    >
      {config.body}
    </EnforcementCard>
  );
});
